import { EmojiPickerDropdown } from "@/components/emoji/EmojiPickerDropdown.tsx";
import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { selectScaleOptionSchema } from "@/components/forms/form-editor.types.ts";
import { ApiFormElementSelectOptionEdit } from "@/types/forms/form-edit.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Avatar,
  Box,
  Button,
  FontAwesomeIcon,
  Input,
  Modal,
  ModalProps,
  Stack,
  Switch,
  Textarea,
  useModals,
} from "@skailer-com/uikit";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface FormEditScaleOrSelectOptionModalProps extends ModalProps {
  isSelect: boolean;
  item: ApiFormElementSelectOptionEdit;
  onSave: (item: ApiFormElementSelectOptionEdit) => Promise<void>;
}

export function FormEditScaleOrSelectOptionModal({
  item,
  onSave,
  isSelect,
  ...props
}: FormEditScaleOrSelectOptionModalProps) {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<
    z.input<typeof selectScaleOptionSchema>,
    unknown,
    z.output<typeof selectScaleOptionSchema>
  >({
    mode: "onSubmit",
    resolver: zodResolver(selectScaleOptionSchema),
    values: {
      title: item.title,
      emoji: item.emoji ?? emojiDefaults.survey.default,
      description: item.description,
      value: item.value,
    },
  });

  const [isUseEmoji, setIsUseEmoji] = useState<boolean>(!!item.emoji);

  const onSubmit = handleSubmit(async (data) => {
    await onSave({
      ...item,
      title: data.title,
      emoji: isUseEmoji ? data.emoji : null,
      description: data.description,
      value: data.value,
    });
    close(id);
  });

  return (
    <>
      <Modal
        {...props}
        title={t("common:option")}
        withCloser
        actions={
          <Stack gap={"sm"}>
            <Button type={"submit"} size={"lg"} onClick={onSubmit}>
              {t("common:save")}
            </Button>
          </Stack>
        }
      >
        <form onSubmit={onSubmit}>
          <Stack gap={"md"}>
            <Stack>
              <Input
                label={t("common:title")}
                autoFocus={true}
                type="text"
                size={"lg"}
                {...register("title")}
                error={errors.title?.message}
              />
              <Textarea
                label={t("common:description")}
                {...register("description")}
                error={errors.description?.message}
              />
              {!isSelect && (
                <Input
                  label={t("common:value_number")}
                  type="number"
                  {...register("value")}
                  error={errors.value?.message}
                />
              )}
            </Stack>
            <Stack gap={"md"}>
              <Switch
                checked={isUseEmoji}
                onChange={setIsUseEmoji}
                title={t("surveys:element_add_emoji")}
              />
              {isUseEmoji && (
                <Controller
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <EmojiPickerDropdown
                      onEmojiSelect={(emoji) => {
                        onChange(emoji);
                      }}
                    >
                      <Box className={"relative w-41 h-41 group cursor-pointer "}>
                        <Avatar variant={"opaque"} url={undefined} emoji={value} size={"2xl"} />
                        <Box
                          className={
                            "bg-dark/20 opacity-0 group-hover:opacity-100 absolute inset-0 rounded-lg flex items-center justify-center"
                          }
                        >
                          <FontAwesomeIcon
                            icon={"fa-light fa-icons"}
                            className={"text-[74px] text-light "}
                          />
                        </Box>
                      </Box>
                    </EmojiPickerDropdown>
                  )}
                  name={"emoji"}
                />
              )}
            </Stack>
          </Stack>
        </form>
      </Modal>
    </>
  );
}
